import { FieldError } from "@home-health-notify/domain";
import dayjs, { Dayjs } from "dayjs";

// Times are "HH:mm" (24h) — the API's format. Zero-padded, so they compare as
// strings. null means the picker is empty or holds an invalid time.
export interface IOrgSettingsForm {
  start: string | null;
  end: string | null;
  reminder: string | null;
}

export type OrgSettingsErrorKey =
  | "required"
  | "startBeforeEnd"
  | "outsideWorkingHours";

export type OrgSettingsErrors = Partial<
  Record<keyof IOrgSettingsForm, OrgSettingsErrorKey>
>;

// Mirrors the backend rules so the form rejects what the API would.
export const validateOrgSettings = ({
  start,
  end,
  reminder,
}: IOrgSettingsForm): OrgSettingsErrors => {
  const errors: OrgSettingsErrors = {};
  if (!start) errors.start = "required";
  if (!end) errors.end = "required";
  if (!reminder) errors.reminder = "required";
  if (!start || !end) return errors;

  if (start >= end) {
    errors.end = "startBeforeEnd";
  } else if (reminder && (reminder < start || reminder > end)) {
    errors.reminder = "outsideWorkingHours";
  }
  return errors;
};

const SERVER_ERRORS: Record<
  string,
  { field: keyof IOrgSettingsForm; error: OrgSettingsErrorKey }
> = {
  "workingHours:INVALID": { field: "end", error: "startBeforeEnd" },
  "preferredReminderTime:OUT_OF_WORKING_HOURS": {
    field: "reminder",
    error: "outsideWorkingHours",
  },
};

export const serverErrorsToForm = (
  fieldErrors: FieldError[],
): OrgSettingsErrors =>
  fieldErrors.reduce<OrgSettingsErrors>((errors, { field, code }) => {
    const mapped = SERVER_ERRORS[`${field}:${code}`];
    return mapped ? { ...errors, [mapped.field]: mapped.error } : errors;
  }, {});

export const toPickerValue = (time: string | null): Dayjs | null => {
  if (!time) return null;
  const [hour, minute] = time.split(":").map(Number);
  return dayjs().startOf("day").hour(hour).minute(minute);
};

export const fromPickerValue = (value: Dayjs | null): string | null =>
  value?.isValid() ? value.format("HH:mm") : null;

export const formatTime = (time: string): string =>
  toPickerValue(time)?.format("h:mm A") ?? time;
